import _ from 'lodash';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Form, Field } from 'react-final-form';
import createDecorator from "final-form-calculate";
import moment from 'moment';

import ReactTableFilterable from '../../components/ReactTableFilterable';
import { FormErrors, DatePicker, Select, TextInput, SubmitButton, reportRules } from '../../components/Forms';
import { fetchRegionList, fetchStateList, fetchWholesalerList, fetchProductList, fetchSalesSummaryReport } from '../../actions';
import {CSVFromTable} from '../../components/ExportCSV';

// variables for table
const wholesalerColumn = {
  Header: 'Wholesaler',
  accessor: 'sales_summary.wholesaler.name',
  minWidth: 150
};
const regionColumn = {
  Header: 'Region',
  accessor: 'sales_summary.wholesaler.region.region',
  minWidth: 100
};
const stateColumn = {
  Header: 'State',
  accessor: 'sales_summary.wholesaler.state.state',
  minWidth: 100
};
const productColumn = {
  Header: 'Product',
  accessor: 'product.name',
  minWidth: 200
};
const brandColumn = {
  Header: 'Brand',
  accessor: 'product.brand',
  minWidth: 100
};
const subBrandColumn = {
  Header: 'Sub-brand',
  accessor: 'product.sub_brand',
  minWidth: 150
};
const skuColumn = {
  Header: 'SKU No.',
  accessor: 'product.sku_no',
  minWidth: 100
};
const actualUnitsColumn = {
  Header: 'Actual Units Sold',
  accessor: 'actual_units',
  Cell: row => (row.value || 0),
  minWidth: 100
};
const standardTOColumn = {
  Header: 'Standard Trade Offer',
  accessor: 'standard_to_units',
  Cell: row => (row.value || 0),
  minWidth: 100
};
const additionalTOColumn = {
  Header: 'Additional Trade Offer',
  accessor: 'additional_to_units',
  Cell: row => (row.value || 0),
  minWidth: 100
};
const countryColumn = {
  Header: 'Country',
  accessor: 'country',
  minWidth: 100
};
const defaultAdminColumns = [wholesalerColumn, stateColumn, regionColumn, productColumn, brandColumn, subBrandColumn, skuColumn, actualUnitsColumn, standardTOColumn, additionalTOColumn];

// Validate form
let formRules = _.cloneDeep(reportRules);

const validateSalesReport = (values) => {
  const {start_date, end_date} = values;
  let errors = {};

  if(moment(start_date) > moment(end_date)) {
    formRules.date_range.valid = false;
    errors.start_date = true;
    errors.end_date = true;
  } else {
    formRules.date_range.valid = true;
  }

  return errors;
}

// Calculated wholesaler field
const calculator = createDecorator({
  field: 'region',
  updates: {
    wholesaler: function(region, {wholesaler}) {
      wholesaler = wholesaler || [];
      return wholesaler.filter(ws => !region || region.length === 0 || region.find(r => r.id === _.get(ws, 'region.id')));
    }
  }
}, {
  field: 'state',
  updates: {
    wholesaler: function(state, {wholesaler}) {
      wholesaler = wholesaler || [];
      return wholesaler.filter(ws => !state || state.length === 0 || state.find(s => s.id === _.get(ws, 'state.id')));
    }
  }
});

const monthStart = moment().startOf('month').toDate();
const monthEnd = moment().endOf('month').toDate();

const wholesalerGroupings = {
  wholesaler: {
    label: 'Wholesaler',
    value: 'sales_summary.wholesaler.name'
  },
  state: {
    label: 'State',
    value: 'sales_summary.wholesaler.state.state'
  },
  region: {
    label: 'Region',
    value: 'sales_summary.wholesaler.region.region'
  },
  country: {
    label: 'Country',
    value: 'country'
  }
};
const defaultWholesalerGrouping = wholesalerGroupings.wholesaler;
const productGroupings = {
  product: {
    label: 'Product',
    value: 'product.name'
  },
  subBrand: {
    label: 'Sub-Brand',
    value: 'product.sub_brand'
  }, 
  brand: {
    label: 'Brand',
    value: 'product.brand'
  }
};
const defaultProductGrouping = productGroupings.product;

const WholesalerSalesSummaryReport = (props) => {
  const authToken = useSelector(({auth}) => auth.authToken);
  const isAdmin = useSelector(({auth}) => auth.isAdmin);
  const isLoading = useSelector(({sales}) => sales.isLoading);

  const dispatch = useDispatch();

  const [adminColumns, setAdminColumns] = useState(defaultAdminColumns);
  const [filterStartDate, setFilterStartDate] = useState(monthStart);
  const [filterEndDate, setFilterEndDate] = useState(monthEnd);
  const [filterRegions, setFilterRegions] = useState([]);
  const [filterStates, setFilterStates] = useState([]);
  const [filterWholesalers, setFilterWholesalers] = useState([]);
  const [filterBrands, setFilterBrands] = useState('');
  const [filterSubBrands, setFilterSubBrands] = useState('');
  const [filterProducts, setFilterProducts] = useState([]);
  const [groupByWholesaler, setGroupByWholesaler] = useState(defaultWholesalerGrouping);
  const [groupByProduct, setGroupByProduct] = useState(defaultProductGrouping);
  useEffect(() => {
    if(authToken) {
      const startDate = moment(filterStartDate).format('YYYY-MM-DD[T00:00:00]');
      const endDate = moment(filterEndDate).format('YYYY-MM-DD[T00:00:00]');
      if(isAdmin) {
        dispatch(fetchSalesSummaryReport(authToken, isAdmin, startDate, endDate));
      }
    }
  }, [filterStartDate, filterEndDate, authToken, isAdmin, dispatch]);
  const data = useSelector(({sales}) => sales.salesSummaryReport) || [];

  useEffect(() => {
    if(authToken) {
      if(isAdmin) {
        dispatch(fetchRegionList(authToken));
        dispatch(fetchStateList(authToken));
        dispatch(fetchWholesalerList(authToken, isAdmin));
        dispatch(fetchProductList(authToken));
      }
    }
  }, [authToken, isAdmin, dispatch]);
  const regionList = useSelector(({master}) => master.regionList);
  const stateList = useSelector(({master}) => master.stateList);
  const wholesalerList = useSelector(({wholesaler}) => wholesaler.wholesalerList);
  const productList = useSelector(({product}) => product.productList);
  const isLoadingMaster = useSelector(({master, wholesaler, outlet, product}) => master.isLoading || wholesaler.isLoading || outlet.isLoading || product.isLoading);

  // Prepare data
  const filteredData = useMemo(() => {
    const reformatted = data.map(sale => isAdmin
      ? {...sale, country: "Malaysia", sales_summary: {...sale.sales_summary, wholesaler: wholesalerList.find(ws => ws.id === _.get(sale, 'sales_summary.wholesaler_id'))}}
      : sale
    );

    const filtered = reformatted.filter(sale => 
      (filterRegions.length === 0 || _.find(filterRegions, r => r.id === _.get(sale, 'sales_summary.wholesaler.region.id'))) &&
      (filterStates.length === 0 || _.find(filterStates, s => s.id === _.get(sale, 'sales_summary.wholesaler.state.id'))) &&
      (filterWholesalers.length === 0 || _.find(filterWholesalers, ws => ws.id === _.get(sale, 'sales_summary.wholesaler.id'))) &&
      (!filterBrands || _.includes(_.get(sale, 'product.brand', '').toLowerCase(), filterBrands.toLowerCase())) &&
      (!filterSubBrands || _.includes(_.get(sale, 'product.sub_brand', '').toLowerCase(), filterSubBrands.toLowerCase())) &&
      (filterProducts.length === 0 || _.find(filterProducts, p => p.id === _.get(sale, 'product.id')))
    );

    // Summarised data
    const summarised = [...filtered.reduce((r, ss) => {
      const key = _.get(ss, groupByWholesaler.value) + '-separator-' + _.get(ss, groupByProduct.value);
      
      const item = r.get(key) || Object.assign({}, ss, {
        actual_units: 0,
        standard_to_units: 0,
        additional_to_units: 0
      });
      item.actual_units += ss.actual_units;
      item.standard_to_units += ss.standard_to_units;
      item.additional_to_units += ss.additional_to_units;
    
      return r.set(key, item);
    }, new Map()).values()];

    return summarised;
  }, [data, isAdmin, wholesalerList, filterRegions, filterStates, filterWholesalers, filterBrands, filterSubBrands, filterProducts, groupByWholesaler.value, groupByProduct.value]);

  const dataTableRef = useRef(null);

  if(!authToken)
    return null;

  return (
    <div>
      <nav className="breadcrumb" aria-label="breadcrumbs">
        <ul>
          <li><Link to="/">Dashboard</Link></li>
          <li><Link to="/reports">Reports</Link></li>
          <li className="is-active"><a href="# " aria-current="page">Wholesaler Sales Summary Report</a></li>
        </ul>
      </nav>
      <section className="hero">
        <div className="hero-body">
          <div className="container has-text-centered">
            <h1 className="title">
              Wholesaler Sales Summary Report
            </h1>
          </div>
        </div>
      </section>
      <Form
        onSubmit={ values => {
          const {start_date, end_date, region, state, wholesaler, brand, sub_brand, product, wholesalerGroup, productGroup} = values;
          if(start_date !== filterStartDate) {
            setFilterStartDate(start_date);
          }
          if(end_date !== filterEndDate) {
            setFilterEndDate(end_date);
          }
          if(!_.isEqual(region, filterRegions)) {
            setFilterRegions(region || []);
          }
          if(!_.isEqual(state, filterStates)) {
            setFilterStates(state || []);
          }
          if(!_.isEqual(wholesaler, filterWholesalers)) {
            setFilterWholesalers(wholesaler || []);
          }
          if(brand !== filterBrands) {
            setFilterBrands(brand);
          }
          if(sub_brand !== filterSubBrands) {
            setFilterSubBrands(sub_brand);
          }
          if(!_.isEqual(product, filterProducts)) {
            setFilterProducts(product || []);
          }
          if(wholesalerGroup !== groupByWholesaler || productGroup !== groupByProduct) {
            if(wholesalerGroup !== groupByWholesaler) {
              setGroupByWholesaler(wholesalerGroup);
            }
            if(productGroup !== groupByProduct) {
              setGroupByProduct(productGroup);
            }

            // Set Columns
            var columns = [...defaultAdminColumns];

            if(wholesalerGroup !== wholesalerGroupings.wholesaler) {
              _.pull(columns, wholesalerColumn);
              if(wholesalerGroup !== wholesalerGroupings.state) {
                _.pull(columns, stateColumn);
                if(wholesalerGroup !== wholesalerGroupings.region) {
                  _.pull(columns, regionColumn);
                  columns = [countryColumn, ...columns];
                }
              }
            }
            if(productGroup !== productGroupings.product) {
              _.pull(columns, productColumn, skuColumn);
              if(productGroup !== productGroupings.subBrand) {
                _.pull(columns, subBrandColumn);
              }
            }
            
            setAdminColumns(columns);
          }
        }}
        initialValues={{
          start_date: monthStart,
          end_date: monthEnd,
          region: [],
          state: [],
          wholesaler: [],
          brand: '',
          sub_brand: '',
          product: [],
          wholesalerGroup: defaultWholesalerGrouping,
          productGroup: defaultProductGrouping
        }}
        keepDirtyOnReinitialize
        decorators={[calculator]}
        validate={validateSalesReport}
        render={({
          handleSubmit,
          form,
          submitting,
          pristine,
          validating,
          values
        }) => (
          <form onSubmit={handleSubmit}>
            <FormErrors formRules={Object.values(formRules)}/>
            <Field name="start_date">
              {(props) => (
                <DatePicker
                  label="From Date"
                  dateFormat="yyyy-MM-dd"
                  selected={moment(props.input.value).toDate()}
                  {...props}
                />
              )}
            </Field>
            <Field name="end_date">
              {(props) => (
                <DatePicker
                  label="To Date"
                  dateFormat="yyyy-MM-dd"
                  selected={moment(props.input.value).toDate()}
                  {...props}
                />
              )}
            </Field>
            { isAdmin
              ? <>
                  <Field name="region">
                    {(props) => (
                      <Select
                        label="Wholesaler Region"
                        options={regionList}
                        getOptionLabel={o => o.region}
                        getOptionValue={o => o.id}
                        placeholder="All"
                        isLoading={isLoadingMaster || isLoading}
                        isMulti
                        isClearable
                        isSearchable
                        {...props}
                      />
                    )}
                  </Field>
                  <Field name="state">
                    {(props) => (
                      <Select
                        label="Wholesaler State"
                        options={stateList}
                        getOptionLabel={o => o.state}
                        getOptionValue={o => o.id}
                        placeholder="All"
                        isLoading={isLoadingMaster || isLoading}
                        isMulti
                        isClearable
                        isSearchable
                        {...props}
                      />
                    )}
                  </Field>
                  <Field name="wholesaler">
                    {(props) => (
                      <Select
                        label="Wholesaler"
                        options={wholesalerList.filter(ws => 
                          (!values.region || values.region.length === 0 || _.find(values.region, r => r.id === _.get(ws, 'region.id'))) &&
                          (!values.state || values.state.length === 0 || _.find(values.state, s => s.id === _.get(ws, 'state.id')))
                        )}
                        getOptionLabel={o => o.name}
                        getOptionValue={o => o.id}
                        placeholder="All"
                        isLoading={isLoadingMaster || isLoading}
                        isMulti
                        isClearable
                        isSearchable
                        {...props}
                      />
                    )}
                  </Field>
                </>
              : null
            }
            <Field name="brand" type="text">
              {(props) => (
                <TextInput
                  horizontal
                  label="Product Brand"
                  disabled={isLoading}
                  {...props}
                />
              )}
            </Field>
            <Field name="sub_brand" type="text">
              {(props) => (
                <TextInput
                  horizontal
                  label="Product Sub-Brand"
                  disabled={isLoading}
                  {...props}
                />
              )}
            </Field>
            <Field name="product">
              {(props) => (
                <Select
                  label="Product"
                  options={productList}
                  getOptionLabel={o => o.name}
                  getOptionValue={o => o.id}
                  placeholder="All"
                  isLoading={isLoadingMaster || isLoading}
                  isMulti
                  isClearable
                  isSearchable
                  {...props}
                />
              )}
            </Field>
            <Field name="wholesalerGroup">
              {(props) => (
                <Select
                  label="Group By (Wholesaler)"
                  options={Object.values(wholesalerGroupings)}
                  value={props.input.value}
                  getOptionLabel={o => o.label}
                  getOptionValue={o => o.value}
                  {...props}
                />
              )}
            </Field>
            <Field name="productGroup">
              {(props) => (
                <Select
                  label="Group By (Product)"
                  options={Object.values(productGroupings)}
                  value={props.input.value}
                  getOptionLabel={o => o.label}
                  getOptionValue={o => o.value}
                  {...props}
                />
              )}
            </Field>
            <div className="column is-full has-text-centered" style={{marginBottom: '30px'}}>
              <SubmitButton
                disabled={submitting || validating}
                loading={submitting || validating || isLoading}
              >Filter</SubmitButton>
            </div>
            <div className="column is-full has-text-right" style={{marginBottom: '20px'}}>
              <CSVFromTable
                tableRef={dataTableRef}
                columns={adminColumns}
                fileName="WholesalerSalesSummaryReport"
                loading={submitting || validating || isLoading}
              >
                <span className="icon">
                  <i className="fas fa-file-excel"></i>
                </span>
                <span>Export to Excel</span>
              </CSVFromTable>
            </div>
          </form>
        )}
      />
      <div>
        <ReactTableFilterable
          data={filteredData}
          columns={adminColumns}
          defaultPageSize={10}
          defaultSorted={[
            {
              id: groupByWholesaler.value,
              desc: false
            }, {
              id: groupByProduct.value,
              desc: false
            }
          ]}
          className="-striped -highlight"
          ref={dataTableRef}
        />
      </div>
    </div>
  );
};

export default WholesalerSalesSummaryReport;